import { Alert, MultiSelect, SimpleSelect } from 'corso-admin-ui';
import { Pegasus } from 'corso-types';
import { Controller } from 'react-hook-form';
import { useAutomationFormContext } from '~/features/settings/automations/hooks/useAutomationForm';
import { useFactValues } from '~/features/settings/hooks/useFactValues';

type ShippingTitleKind =
  Pegasus.EventSpcReorderShipping['params']['shippingTitleKind'];

const shippingTitleKindLabel = {
  selection: 'Offer a Selection',
  assignment: 'Assign a Title',
} as const satisfies Record<ShippingTitleKind, string>;

const shippingTitleKindDetails = {
  selection: 'The shipping titles to select from when finalizing a reorder.',
  assignment: 'The shipping title to use for the reorder.',
} as const satisfies Record<ShippingTitleKind, string>;

const shippingTitleKinds = [
  'selection',
  'assignment',
] satisfies ShippingTitleKind[];

const shippingTitleKindOptions = shippingTitleKinds.map((value) => ({
  value,
  label: shippingTitleKindLabel[value],
}));

export function EventShippingClaimReorderShipping() {
  const { control, setValue, watch } = useAutomationFormContext();

  const shippingTitleKind = watch('event.params.shippingTitleKind');

  const factValues = useFactValues();

  const titleOptions = (
    factValues.data?.arbitrary['order/shippingLine/title'] ?? []
  ).map((title) => ({ value: title, label: title, keywords: [title] }));

  return (
    <div className="flex flex-col gap-4">
      <Alert
        variant="tip"
        heading="No rule is required to use the original order's shipping title."
      />
      <Controller
        control={control}
        name="event.params.shippingTitleKind"
        defaultValue="selection"
        render={({ field, fieldState }) => (
          <SimpleSelect
            label="Shipping Title Handling"
            options={shippingTitleKindOptions}
            value={field.value}
            onChange={(kind) => {
              // the branch's field keeps whatever the other kind left behind, so seed it empty first
              if (kind === 'selection')
                setValue('event.params.shippingTitles', []);
              else setValue('event.params.shippingTitle', '');

              field.onChange(kind);
            }}
            details={shippingTitleKindDetails[field.value]}
            error={fieldState.error?.message}
          />
        )}
      />

      {shippingTitleKind === 'selection' ? (
        <Controller
          control={control}
          name="event.params.shippingTitles"
          render={({ field, fieldState }) => (
            <MultiSelect
              label="Shipping Titles"
              details="The shipping titles offered when creating the reorder."
              placeholder="Add shipping titles"
              options={titleOptions}
              // a rule saved as `assignment` has a string here, which the union's type does not admit
              value={(Array.isArray(field.value) ? field.value : []).map(
                (title) => ({ label: title, value: title }),
              )}
              onChange={(selections) =>
                field.onChange(selections.map(({ value }) => value))
              }
              error={fieldState.error?.message}
              creatable
            />
          )}
        />
      ) : (
        <Controller
          control={control}
          name="event.params.shippingTitle"
          render={({ field, fieldState }) => (
            <MultiSelect
              label="Shipping Title"
              details="The shipping title applied to every reorder."
              placeholder="Find or add a shipping title"
              options={titleOptions}
              // a rule saved as `selection` has an array here, which the union's type does not admit
              value={
                typeof field.value === 'string' && field.value
                  ? [{ label: field.value, value: field.value }]
                  : []
              }
              // one title is assigned, so the latest pick replaces the previous one
              onChange={(selections) =>
                field.onChange(selections.at(-1)?.value ?? '')
              }
              error={fieldState.error?.message}
              creatable
            />
          )}
        />
      )}
    </div>
  );
}
